import * as FSPR from "src/generated/hooks/form-service-public/requests";
import { gradeOrderOf, unavailableSeatBandKey } from "./filters";

export type SchoolGroup = {
  schoolId: string;
  schoolName: string;
  rows: FSPR.PublicCapacityRow[];
  /** Distinct grades at this school, so programs are not counted twice. */
  totalGrades: number;
  availableGrades: number;
};

/**
 * Rows arrive ordered by school name then grade order, so inserting into a Map
 * keeps both the school order and the grade order the service chose. Sorting
 * grades again by gradeOrder makes that independent of merge/filter order.
 */
export function groupBySchool(
  rows: FSPR.PublicCapacityRow[],
  seatLegend: FSPR.AvailabilityBandValue[] = []
): SchoolGroup[] {
  const unavailableKey = unavailableSeatBandKey(seatLegend);
  const groups = new Map<string, FSPR.PublicCapacityRow[]>();

  for (const row of rows) {
    const existing = groups.get(row.schoolId);
    if (existing === undefined) {
      groups.set(row.schoolId, [row]);
    } else {
      existing.push(row);
    }
  }

  return [...groups.entries()].map(([schoolId, schoolRows]) => {
    const allGrades = new Set<string>();
    const openGrades = new Set<string>();

    for (const row of schoolRows) {
      allGrades.add(row.gradeId);
      if (unavailableKey === undefined || row.seats.key !== unavailableKey) {
        openGrades.add(row.gradeId);
      }
    }

    const orderedRows = schoolRows.some(
      (row) => gradeOrderOf(row) !== undefined
    )
      ? [...schoolRows].sort(
          (a, b) =>
            (gradeOrderOf(a) ?? 0) - (gradeOrderOf(b) ?? 0) ||
            (a.programLabel ?? "").localeCompare(b.programLabel ?? "")
        )
      : schoolRows;

    return {
      schoolId,
      schoolName: schoolRows[0]?.schoolName ?? "",
      rows: orderedRows,
      totalGrades: allGrades.size,
      availableGrades: openGrades.size,
    };
  });
}
